import React, { useCallback, useRef } from 'react';
import { NodeMouseHandler, OnSelectionChangeFunc, EdgeMouseHandler, Node, Edge } from 'reactflow'; // Import Node, Edge

import { CustomNode } from '../../types';

type CanvasMode = 'project' | 'playbook';

// Define the props the hook needs
export interface InteractionHandlerProps { // Export the type
    isEmbedded: boolean;
    mode: CanvasMode;
    nodes: Node[]; // Use generic Node
    edges: Edge[]; // Add edges array
    selectedEdge: Edge['id'] | null; // Use Edge['id']
    // Accept the union of function types provided by the selector
    setSelectedNode?: ((node: CustomNode | null) => void) | ((node: Node | null) => void);
    setSelectedEdge: (edgeId: Edge['id'] | null) => void; // Use Edge['id'] - Keep for single selection state if needed elsewhere
    setNodes: (nodes: Node[]) => void; // Use generic Node
    setEdges: (edges: Edge[]) => void; // Add setEdges function
}

interface InteractionHandlers {
    onNodeClick: NodeMouseHandler;
    onPaneClick: (event: React.MouseEvent) => void;
    onSelectionChange: OnSelectionChangeFunc;
    onEdgeClick: EdgeMouseHandler;
    selectedNodeIdRef: React.MutableRefObject<string | null>;
    wasClickRef: React.MutableRefObject<boolean>;
}

export const useInteractionHandlers = ({
    isEmbedded,
    mode,
    nodes: currentNodes, // Rename for clarity
    edges: currentEdges, // Add edges
    selectedEdge: currentSelectedEdge, // Rename for clarity
    setSelectedNode: currentSetSelectedNode, // Rename for clarity
    setSelectedEdge: currentSetSelectedEdge, // Rename for clarity
    setNodes: currentSetNodes, // Rename for clarity
    setEdges: currentSetEdges, // Add setEdges
}: InteractionHandlerProps): InteractionHandlers => {
    const selectedNodeIdRef = useRef<string | null>(null);
    const prevSelectionRef = useRef<{ nodesCount: number, edgesCount: number }>({ nodesCount: 0, edgesCount: 0 });
    const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const wasClickRef = useRef<boolean>(false);

    const onNodeClick: NodeMouseHandler = useCallback((event, node) => {
        wasClickRef.current = true;
        event.stopPropagation();
        const nodeId = node.id;
        const isShiftPressed = event.shiftKey;

        // Actions are now passed via props
        if (!currentSetSelectedEdge || !currentSetSelectedNode || !currentSetNodes) {
             console.error(`[Canvas] Missing required ${mode} store actions for node click handler.`);
             return;
        }

        // Deselect edge only if Shift is NOT pressed
        if (!isShiftPressed && currentSelectedEdge !== null) {
            currentSetSelectedEdge(null);
        }

        const currentNode = currentNodes.find(n => n.id === nodeId);

        if (!currentNode) {
            console.warn(`[Canvas] Clicked node not found in ${mode} store:`, nodeId);
            return;
            console.warn(`[Canvas] Clicked node not found in ${mode} store:`, nodeId);
            return;
        }

        if (isShiftPressed) {
             if (currentSelectedEdge !== null) {
                 currentSetSelectedEdge(null);
             }

        } else {
            if (selectedNodeIdRef.current !== nodeId || !currentNode.selected) {
                 selectedNodeIdRef.current = nodeId;
                 currentSetSelectedNode(currentNode as any);
                 if (currentSelectedEdge !== null) {
                     currentSetSelectedEdge(null);
                 }
            }
        }
    }, [mode, currentNodes, currentEdges, currentSelectedEdge, currentSetSelectedEdge, currentSetSelectedNode, currentSetNodes, currentSetEdges]); // Update dependencies


    const onPaneClick = useCallback((event: React.MouseEvent) => {
         // Actions are now passed via props
        if (!currentSetSelectedNode || !currentSetSelectedEdge || !currentSetNodes) {
             console.error(`[Canvas] Missing required ${mode} store actions for pane click handler.`);
             return;
        }

        if (event.target instanceof Element && event.target.closest('.react-flow__node, .react-flow__edge, .react-flow__controls')) {
            return;
        }

        if (currentNodes.some((n: any) => n.selected)) {
            currentSetNodes(currentNodes.map((n: any) => ({ ...n, selected: false })) as any);
        }

        if (selectedNodeIdRef.current !== null) {
            selectedNodeIdRef.current = null;
            currentSetSelectedNode(null);
        }

        if (currentSelectedEdge !== null) {
            currentSetSelectedEdge(null);
        }
    }, [mode, currentNodes, currentSelectedEdge, currentSetSelectedEdge, currentSetSelectedNode, currentSetNodes]); // Update dependencies

    const onSelectionChange: OnSelectionChangeFunc = useCallback(({ nodes: rfNodes, edges: rfEdges }) => {
        // Actions are now passed via props
        if (!currentSetSelectedNode || !currentSetSelectedEdge) {
            console.error(`[Canvas] Missing required ${mode} store actions for selection change handler.`);
            return;
        }

        // Always update the current selection reference
        const currentSelection = { nodesCount: rfNodes.length, edgesCount: rfEdges.length };
        
        // Update the previous selection reference regardless of whether we're debouncing
        // This ensures we always have the latest selection state
        prevSelectionRef.current = currentSelection;

        // Clear any existing debounce timeout
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        // Process the selection change, with special handling for empty selections
        // to ensure they are processed immediately without debouncing
        const processSelectionChange = () => {
            // Node Selection Sync
            if (rfNodes.length === 1) {
                const newSelectedNode = rfNodes[0];
                if (selectedNodeIdRef.current !== newSelectedNode.id) {
                    const storeNode = currentNodes.find(n => n.id === newSelectedNode.id);
                    selectedNodeIdRef.current = newSelectedNode.id;
                    currentSetSelectedNode(storeNode || newSelectedNode as any);
                }
            } else if (rfNodes.length === 0 && selectedNodeIdRef.current !== null) {
                // Only deselect node if no edges are selected either
                if (rfEdges.length === 0) {
                    selectedNodeIdRef.current = null;
                    currentSetSelectedNode(null);
                }
            } else if (rfNodes.length > 1) {
                // Multiple nodes selected, clear single selected node state
                if (selectedNodeIdRef.current !== null) {
                    selectedNodeIdRef.current = null;
                    currentSetSelectedNode(null);
                }
            }

            // Edge Selection Sync (using the passed edges array)
            if (rfEdges.length === 1) {
                const newSelectedEdgeId = rfEdges[0].id;
                // Update the single selected edge state if needed
                if (currentSelectedEdge !== newSelectedEdgeId) {
                    currentSetSelectedEdge(newSelectedEdgeId);
                }
            } else if (rfEdges.length === 0) {
                // Clear single selected edge state if no edges are selected
                if (currentSelectedEdge !== null) {
                    currentSetSelectedEdge(null);
                }
            } else if (rfEdges.length > 1) {
                // Multiple edges selected, clear single selected edge state
                if (currentSelectedEdge !== null) {
                    currentSetSelectedEdge(null);
                }
            }

            // --- Sync Node Selection State ---
            const selectedNodeIds = new Set(rfNodes.map(n => n.id));
            const updatedNodes = currentNodes.map(n => ({
                ...n,
                selected: selectedNodeIds.has(n.id),
            }));
            
            // Check if there's an actual change in node selection before updating
            // For empty selections, we always want to update to ensure proper reset
            const hasNodeSelectionChanged = rfNodes.length === 0 || 
                currentNodes.some(n => n.selected !== selectedNodeIds.has(n.id));
            
            if (hasNodeSelectionChanged) {
                currentSetNodes(updatedNodes);
            }

            // --- Sync Edge Selection State ---
            // When multiple nodes are selected or during drag selection, don't select edges
            if (rfNodes.length > 1) {
                // If multiple nodes are selected, deselect all edges
                const allEdgesDeselected = currentEdges.map(e => ({
                    ...e,
                    selected: false,
                }));
                
                // Only update if there are any selected edges
                if (currentEdges.some(e => e.selected)) {
                    currentSetEdges(allEdgesDeselected);
                }
            } else {
                // Normal edge selection behavior when 0 or 1 node is selected
                const selectedEdgeIds = new Set(rfEdges.map(e => e.id));
                const updatedEdges = currentEdges.map(e => ({
                    ...e,
                    selected: selectedEdgeIds.has(e.id),
                }));
                
                // Check if there's an actual change in edge selection before updating
                // For empty selections, we always want to update to ensure proper reset
                const hasEdgeSelectionChanged = rfNodes.length === 0 || 
                    currentEdges.some(e => e.selected !== selectedEdgeIds.has(e.id));
                
                if (hasEdgeSelectionChanged) {
                    currentSetEdges(updatedEdges);
                }
            }
        };

        // Use a consistent approach for all selection changes to avoid infinite loops
        // Always use setTimeout to ensure we're not causing multiple state updates in the same render cycle
        debounceTimeoutRef.current = setTimeout(() => {
            processSelectionChange();
        }, rfNodes.length === 0 && rfEdges.length === 0 ? 0 : 50);
    }, [mode, currentNodes, currentEdges, currentSelectedEdge, currentSetSelectedNode, currentSetSelectedEdge, currentSetNodes, currentSetEdges]); // Update dependencies


    const onEdgeClick: EdgeMouseHandler = useCallback((event, edge) => {
        if (isEmbedded) return;
        event.stopPropagation();
        const isShiftPressed = event.shiftKey;

        // Actions are now passed via props
        if (!currentSetSelectedNode || !currentSetSelectedEdge || !currentSetNodes || !currentSetEdges || !currentEdges) {
            console.error(`[Canvas] Missing required ${mode} store actions/state for edge click handler.`);
            return;
            return;
        }

        if (isShiftPressed) {
            if (selectedNodeIdRef.current !== null) {
                selectedNodeIdRef.current = null;
                currentSetSelectedNode(null);
            }

        } else {
            if (currentSelectedEdge !== edge.id) {
                currentSetSelectedEdge(edge.id); // Set single selected edge

                // Clear single selected node state
                if (selectedNodeIdRef.current !== null) {
                    selectedNodeIdRef.current = null;
                    currentSetSelectedNode(null);
                }
            }
        }
    }, [isEmbedded, mode, currentNodes, currentEdges, currentSetSelectedNode, currentSetSelectedEdge, currentSetNodes, currentSetEdges]); // Update dependencies


    return {
        onNodeClick,
        onPaneClick,
        onSelectionChange,
        onEdgeClick,
        selectedNodeIdRef,
        wasClickRef,
    };
};
